import { AlarmRow } from "@src/cloud-connect/alarms/entities/alarm-row";
import { formatDateTimeForGraphLabel } from "@src/cloud-connect/utils/format-date-time-for-graph-label";
import store from "@src/reducers/store";
import { formatChartTooltipDate } from "@src/cloud-connect/utils/timezone-display-utils";
import {
  buildTooltipDateHeader,
  buildTooltipAlarmSeriesRow,
} from "@src/cloud-connect/charts/chart-tooltip-utils";
import { ToolboxBuilder } from "@src/cloud-connect/charts/toolbox-builder";
import { GridBuilder } from "@src/cloud-connect/charts/grid-builder";
import { XAxisBuilder } from "@src/cloud-connect/charts/xaxis-builder";

function createDiagonalPattern() {
  const canvas = document.createElement("canvas");
  canvas.width = 10;
  canvas.height = 10;
  const ctx = canvas.getContext("2d");

  // Create diagonal line
  ctx.strokeStyle = "rgb(253, 56, 56, 0.2)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, 10);
  ctx.lineTo(10, 0);
  ctx.stroke();

  return canvas;
}

export const buildGraphOptionObjectAlarmPanel = (
  nodeName,
  valuesArray,
  timesArray,
  /* zoomStart, */
  unitDisplayName,
  /* showDataZoom = false, */
  activeAlarmRow: AlarmRow,
) => {
  const conditions = activeAlarmRow.getAlarm().getCriteria().getConditions();

  const minValueInValuesArray = Math.min(...valuesArray);
  const maxValueInValuesArray = Math.max(...valuesArray);

  let lowerThreshold = null;
  let upperThreshold = null;

  if (conditions.length === 1) {
    // checks on substring
    if (conditions[0]?.getOperator().includes("greater-than")) {
      upperThreshold = conditions[0]?.getUserInput()
        ? Number(conditions[0].getUserInput())
        : null;
    } else {
      lowerThreshold = conditions[0]?.getUserInput()
        ? Number(conditions[0].getUserInput())
        : null;
    }
  } else if (conditions.length === 2) {
    lowerThreshold = conditions[0]?.getUserInput()
      ? Number(conditions[0].getUserInput())
      : null;

    upperThreshold = conditions[1]?.getUserInput()
      ? Number(conditions[1].getUserInput())
      : null;
  }

  const isLowerThresholdLowerThanMinValue =
    lowerThreshold && lowerThreshold < minValueInValuesArray;

  const isUpperThresholdHigherThanMaxValue =
    upperThreshold && upperThreshold > maxValueInValuesArray;

  const isAlarmActive = activeAlarmRow.getAlarm().getActive();

  const graphOptions = {
    grid: new GridBuilder().setRight(20).setBottom(40).build(),
    toolbox: new ToolboxBuilder()
      .withDataView()
      .withSaveAsImage({ name: nodeName })
      .build(),
    xAxis: new XAxisBuilder()
      .setData(timesArray)
      .setFormatter(formatDateTimeForGraphLabel)
      .build(),
    yAxis: {
      name: unitDisplayName,
      type: "value",
      min:
        lowerThreshold && isLowerThresholdLowerThanMinValue
          ? lowerThreshold
          : null,
      max:
        upperThreshold && isUpperThresholdHigherThanMaxValue
          ? upperThreshold
          : null,
      scale: true,
      axisLine: {
        onZero: true,
      },
      nameTextStyle: {
        padding: [-11, 0, 0, -20],
        verticalAlign: "top",
        align: "left",
      },
      /* axisLabel: {
        formatter: manipulateValue,
      }, */
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(255, 255, 255, 0.3)", // Semi-transparent background
      confine: true,
      borderColor: "gray",
      borderWidth: 1,
      textStyle: {
        fontFamily: "Inter", // Custom font family (or replace with preferred font)
      },
      formatter: function (params) {
        const timezone = store.getState().yourSlice.timezone;
        const language = store.getState().yourSlice.language;
        const formattedDate = formatChartTooltipDate(
          params[0].axisValue,
          timezone,
          language,
        );

        let tooltipHTML = buildTooltipDateHeader(formattedDate);

        params.forEach((item) => {
          tooltipHTML += buildTooltipAlarmSeriesRow({
            marker: item.marker,
            value: item.data,
          });
        });

        return tooltipHTML;
      },
      extraCssText:
        "backdrop-filter: blur(5px); padding: 15px 20px 10px; box-sizing: border-box;",
    },
    /* dataZoom: [
      {
        show: showDataZoom,
        realtime: true,
        start: zoomStart,
        end: 100,
        xAxisIndex: [0, 1],
        textStyle: {
          color: "transparent",
          fontSize: 0,
        },
      },
      {
        show: showDataZoom,
        realtime: true,
        start: zoomStart,
        end: 100,
        xAxisIndex: [0, 1],
        textStyle: {
          color: "transparent",
          fontSize: 0,
        },
      },
    ], */
    series: {
      data: valuesArray,
      type: "line",
      smooth: 0.4,
      lineStyle: {
        color: "#027680",
        width: 2,
      },
      symbol: "circle",
      symbolSize: 8,
      showSymbol: valuesArray.length === 1 ? true : false,
      itemStyle: {
        color: "#3aacc8",
      },
      animation: true,
      animationDuration: 1000,
      animationEasing: "quadraticln",
      animationDelay: 0,
      connectNulls: true,
      ...(isAlarmActive && {
        markLine: {
          data: [
            ...(upperThreshold !== null
              ? [{ yAxis: upperThreshold, name: "Upper Threshold" }]
              : []),
            ...(lowerThreshold !== null
              ? [{ yAxis: lowerThreshold, name: "Lower Threshold" }]
              : []),
          ],
          lineStyle: {
            color: "red",
            type: "dashed",
            opacity: 0.6,
          },
          symbol: "none",
          label: {
            show: false,
            position: "end",
            formatter: "{c}",
            fontSize: 12,
            color: "#fd3838",
          },
          animation: false,
        },
        markArea: {
          itemStyle: {
            color: {
              type: "pattern",
              image: createDiagonalPattern(), // Function to generate the diagonal pattern
              repeat: "repeat",
            },
          },
          data: [
            ...(upperThreshold !== null
              ? [
                  [
                    { yAxis: upperThreshold },
                    isUpperThresholdHigherThanMaxValue
                      ? { yAxis: upperThreshold }
                      : { coord: "max" },
                  ],
                ]
              : []),
            ...(lowerThreshold !== null
              ? [
                  [
                    isLowerThresholdLowerThanMinValue
                      ? { yAxis: lowerThreshold }
                      : { coord: "min" },
                    { yAxis: lowerThreshold },
                  ],
                ]
              : []),
          ],
        },
      }),
    },
  };

  return graphOptions;
};
